import '@/styles/globals.v2.css';

import Image from 'next/image';

import Button from '@/components/V2/Button';
import useWebAlias from '@/helpers/hooks/useWebalias';
import { generateApproperiateURL, getCartUrlWithRecommendationParam } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';

import '@/styles/globals.v2.css';

import cartArrowIcon from '@/assets/icons/cart-dropdown-icon.svg';
import { CartItem } from '@/types/Cart';

import './style.css';

import { useEffect, useMemo, useRef, useState } from 'react';

import { usePathname, useRouter } from 'next/navigation';
import NProgress from 'nprogress';

import searchIcon from '@/assets/icons/search-icon-boxed.svg';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import { CustomerTypes } from '@/helpers/constants/shopping';
import useDetectScrollOutside from '@/helpers/hooks/useDetectScrollOutside';
import cn from '@/helpers/utils/classNames';
import { getFreeShippingMessageByCount } from '@/helpers/utils/common';
import { findEnrollmentPackItemsInCart } from '@/helpers/utils/enrollmentFlowTracker';
import { roundToTwoDecimalPlaces } from '@/helpers/utils/numberHelpers';
import { useAppSelector } from '@/store';

import DeadState from '../DeadState';
import Product from './Product';

const ENROLLMENT_CHECKOUT_ROUTE = '/enrollment/enrolleeinfo';
const CHECKOUT_PREORDER_ROUTE = '/checkout/preorder';
const RECOMMENDATION_FLOW_ROUTE = 'petprofile/createrecommendationnew';

export default function CartDropdown({
  children,
  carts,
  totalItems,
  qualifiedItemsCount,
  isShortHeaderCart = false,
  setShowChooseYourCheckoutFlowModal,
}: {
  children: React.ReactNode;
  carts?: CartItem[];
  totalItems: number;
  qualifiedItemsCount: number;
  isShortHeaderCart?: boolean;
  setShowChooseYourCheckoutFlowModal?: (show: boolean) => void;
}) {
  const divRef = useRef<HTMLDivElement | null>(null);
  const [distanceToTop, setDistanceToTop] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const { webalias } = useWebAlias();
  const { isBackOffice } = useAppContext();
  const { customer } = useAuthContext();
  const isAuthenticated = useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);
  const pathname = usePathname();
  const router = useRouter();

  // Check if user is already a petPro (customerTypeID === 3) - don't show modal for petPros
  const isPetPro = customer?.customerTypeID === CustomerTypes.PET_PRO;

  // Check if user is on a checkout page (disable proceed to checkout button)
  const isOnCheckoutPage = pathname?.includes(CHECKOUT_PREORDER_ROUTE) || pathname?.includes(ENROLLMENT_CHECKOUT_ROUTE);

  // Check if user has visited recommendation flow (from sessionStorage, URL param, or currently on recommendation route)
  // Re-check when pathname changes or when dropdown opens to ensure we have the latest value
  const isFromPetRecommendations = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const hasVisitedRecommendationFlow = sessionStorage.getItem('hasVisitedRecommendationFlow') === 'true';
    const urlParams = new URLSearchParams(window.location.search);
    const isFromRecommendationParam = urlParams.get('isFromPetRecommendations') === 'true';
    // Also check if user is currently on the recommendation flow route
    const isOnRecommendationRoute = window.location.pathname.includes(RECOMMENDATION_FLOW_ROUTE);
    return hasVisitedRecommendationFlow || isFromRecommendationParam || isOnRecommendationRoute;
  }, [pathname, isOpen]);

  // Check if cart has enrollment pack items
  const hasEnrollmentPackItems = useMemo(() => {
    if (!carts || carts.length === 0) return false;
    return findEnrollmentPackItemsInCart(carts).length > 0;
  }, [carts]);

  const subTotal = () => {
    const a = carts ? carts.reduce((acc, cart) => acc + Number(cart.totalPrice), 0) : 0;
    return roundToTwoDecimalPlaces(a);
  };

  const handleProceedToCheckout = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    // If on checkout page, do nothing (button should be disabled)
    if (isOnCheckoutPage) {
      return;
    }

    // If cart is empty, redirect to products page
    if (totalItems === 0) {
      sessionStorage.removeItem('hasVisitedRecommendationFlow');
      router.push(generateApproperiateURL('/products', isBackOffice ? 'shopping' : webalias));
      return;
    }

    // If user came from recommendation flow AND is not already a petPro, show the checkout experience modal
    // petPros (customerTypeID === 3) should go directly to checkout without seeing the modal
    if (isFromPetRecommendations && !isPetPro && setShowChooseYourCheckoutFlowModal) {
      NProgress.done();
      setShowChooseYourCheckoutFlowModal(true);
      setIsOpen(false);
      return;
    }

    // If cart has enrollment pack items (and NOT from recommendation flow), go to enrollment checkout
    if (hasEnrollmentPackItems) {
      router.push(generateApproperiateURL(ENROLLMENT_CHECKOUT_ROUTE, isBackOffice ? 'shopping' : webalias));
      return;
    }

    // Default: go to regular checkout
    localStorage.removeItem('petProfile');
    router.push(generateApproperiateURL(proceedToCheckoutUrl, isBackOffice ? 'shopping' : webalias));
  };

  useEffect(() => {
    const gap = isShortHeaderCart ? 50 : 99;
    if (divRef && divRef.current) {
      const top = divRef.current.getBoundingClientRect().top;
      setDistanceToTop(top + gap);
    }
  }, [isOpen, isShortHeaderCart]);

  useDetectScrollOutside(() => {
    setIsOpen(false);
  }, divRef);

  const proceedToCheckoutUrl = totalItems > 0 ? '/checkout/preorder?v=' + Date.now() : '/products';
  const cartUrl = getCartUrlWithRecommendationParam(isAuthenticated);

  return (
    <>
      {isOpen && (
        <div
          className={cn(
            'v2-fixed v2-top-[60px] v2-left-0 v2-right-0 v2-bottom-0 v2-z-[3] v2-bg-[#0C111D] v2-bg-opacity-[0.35]'
          )}
          style={{ top: `${distanceToTop}px` }}
        />
      )}
      <div className="v2-relative peer" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
        <div ref={divRef}>{children}</div>

        <div
          className={cn(
            'v2-bg-white v2-rounded-xs v2-border v2-w-[320px] md:v2-w-[400px] v2-absolute v2-top-[20px] md:v2-top-[27px] v2-z-[3] v2-right-0 v2-hidden peer-hover:!v2-block',
            { '!v2-block': isOpen }
          )}
        >
          <div className="v2-mt-8 v2-pb-3 v2-px-6 md:v2-px-8">
            <div className="v2-flex v2-items-center v2-justify-between v2-mb-6 v2-gap-5 md:v2-gap-10">
              <div className="v2-w-[160px] md:v2-w-[200px]">
                <p className="v2-text-[#101828] v2-font-sweet-sans-pro v2-text-lg md:v2-text-2xl">
                  Subtotal: ${subTotal()}
                </p>
                <span className="v2-text-[#123E2E] v2-font-sweet-sans-pro-medium v2-text-xs text-wrap-auto">
                  {getFreeShippingMessageByCount(qualifiedItemsCount)}
                </span>
              </div>
              <Button
                type="button"
                btnSize="md"
                variant="filled"
                scheme="text"
                as="Link"
                className="!v2-text-[#123E2E] !v2-bg-transparent hover:!v2-bg-transparent v2-flex v2-gap-[6px] !v2-p-0"
                href={generateApproperiateURL(cartUrl, isBackOffice ? 'shopping' : webalias)}
              >
                View Cart
                <Image src={cartArrowIcon} alt="" />
              </Button>
            </div>
            <Button
              type="button"
              btnSize="md"
              variant="filled"
              scheme="primary"
              className="!v2-w-full v2-text-center"
              onClick={handleProceedToCheckout}
              disabled={isOnCheckoutPage}
            >
              Proceed to Checkout
            </Button>
          </div>
          <div className="v2-border-t v2-px-6 v2-h-[250px] md:v2-h-[432px] v2-overflow-hidden v2-overflow-y-auto v2-py-3 v2-divide-y cart-products-dropdown-container">
            {carts && carts.length > 0 ? (
              carts.map((item, index) => <Product key={index} {...item} />)
            ) : (
              <DeadState
                title="No Products in Your Cart"
                icon={searchIcon}
                className="!v2-w-[270px] !v2-h-[200px] sm:!v2-h-[300px]"
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
